import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next'; // 🎯 الترجمة
import { Plus, Edit3, Trash2, Settings, Zap, Download, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useFontSize } from './FontSizeContext';

const WorkflowList = ({ onEdit }) => {
  const { t, i18n } = useTranslation();
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();   
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;

  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

  // 1. دالة جلب البيانات من الباكيند
  const fetchWorkflows = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/api/workflow/processes', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setWorkflows(res.data);
    } catch (err) {
      toast.error(t('workflow.toasts.fetch_fail'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkflows();
  }, []);

  // 2. دالة الاكسبورت (CSV Export)
  const exportToCSV = () => {
    if (workflows.length === 0) return toast.error(t('workflow.toasts.no_data'));
    
    // العناوين مترجمة في ملف الإكسل
    const headers = `${t('workflow.table.id')},${t('workflow.table.name')},${t('workflow.table.app')},${t('workflow.table.status')},${t('workflow.table.created')}\n`;
    const rows = workflows.map(wf => 
      `${wf.id},${wf.process_name},${wf.object_name},${wf.is_active ? t('common.active') : t('common.inactive')},${wf.created_at || ''}`
    ).join("\n");

    const blob = new Blob([headers + rows], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", `workflows_${new Date().toLocaleDateString()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className={`p-6 bg-[#f8f9fb] h-screen overflow-y-auto ${isRTL ? 'font-cairo text-right' : 'font-sans text-left'}`} style={{ fontSize: `${fontSize}px` }}>
      {/* Header */}
      <div className={`flex justify-between items-center mb-8 bg-white p-4 rounded-xl shadow-sm border border-slate-100`}>
        <div className={isRTL ? 'text-right' : 'text-left'}>
          <h1 className={`text-2xl font-black text-[#1a3353] uppercase tracking-tighter flex items-center gap-2 `}>
            <Zap className="text-blue-600" /> {t('workflow.title')}
          </h1>
          <p className="text-gray-400 text-[10px] font-bold uppercase tracking-widest">{t('workflow.subtitle')}</p>
        </div>
        <div className={`flex gap-3`}>
          <button onClick={exportToCSV} className="border border-slate-200 text-slate-600 px-4 py-2 rounded-lg font-black text-[1.0em] flex items-center gap-2 hover:bg-slate-50 transition-all uppercase">
            <Download size={14}/> {t('workflow.export_btn')}
          </button>
          <button onClick={() => navigate('/workflow/new')} className="bg-[#1a3353] text-white px-6 py-2 rounded-lg shadow-lg font-black text-[1.0em] flex items-center gap-2 hover:bg-black transition-all uppercase">
            <Plus size={16}/> {t('workflow.new_process')}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center h-64 opacity-50">
          <RefreshCw className="animate-spin mb-2" size={32} />
          <span className="font-black text-[1.0em] uppercase">{t('workflow.loading')}</span>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-in fade-in duration-500">
          {workflows.map(wf => (
            <div key={wf.id} className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all group relative overflow-hidden">
               <div className={`absolute top-0 ${isRTL ? 'left-0 -ml-8' : 'right-0 -mr-8'} -mt-8 w-24 h-24 rounded-full opacity-5 ${wf.is_active ? 'bg-green-500' : 'bg-red-500'}`}></div>

              <div className={`flex justify-between items-start mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`p-3 rounded-xl ${wf.is_active ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                  <Zap size={24} fill="currentColor"/>
                </div>
                <div className={`flex flex-col ${isRTL ? 'items-start' : 'items-end'}`}>
                  <span className={`text-[0.9em] font-black px-3 py-1 rounded-full ${wf.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {wf.is_active ? t('common.active') : t('common.inactive')}
                  </span>
                  <span className="text-[0.8em] text-slate-400 mt-1 font-mono">v.{wf.version || 1.0}</span>
                </div>
              </div>

              <h3 className="font-black text-[#1a3353] text-xl mb-1 group-hover:text-blue-600 transition-colors uppercase truncate">
                {wf.process_name || t('workflow.unnamed')}
              </h3>
              <p className="text-slate-500 text-xs mb-4 line-clamp-2 italic h-8">
                {wf.description || t('workflow.no_desc')} 
              </p>
              <p className={`text-slate-400 text-[1.0em] font-bold uppercase mb-6 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                {t('workflow.linked_to')} <span className="text-orange-600 bg-orange-50 px-2 py-0.5 rounded">{wf.object_name}</span>
              </p>
              
              <div className="flex gap-2 border-t border-slate-50 pt-5">
                <button onClick={() => navigate(`/workflow/${wf.id}`)} className="flex-1 flex justify-center py-2.5 hover:bg-blue-50 rounded-xl text-slate-400 hover:text-blue-600 border border-transparent hover:border-blue-100 transition-all" title={t('workflow.tooltips.edit')}>
                  <Edit3 size={18}/>
                </button>
                <button className="flex-1 flex justify-center py-2.5 hover:bg-slate-50 rounded-xl text-slate-400 hover:text-slate-900 border border-transparent hover:border-slate-200 transition-all" title={t('workflow.tooltips.settings')}>
                  <Settings size={18}/>
                </button>
                <button className="flex-1 flex justify-center py-2.5 hover:bg-red-50 rounded-xl text-slate-400 hover:text-red-600 border border-transparent hover:border-red-100 transition-all" title={t('workflow.tooltips.delete')}>
                  <Trash2 size={18}/>
                </button>
              </div>
            </div>
          ))}

          {/* Quick Add Card */}
          <div onClick={() => navigate('/workflow/new')} className="border-2 border-dashed border-slate-200 rounded-2xl p-6 flex flex-col items-center justify-center text-slate-300 hover:border-blue-400 hover:text-blue-400 cursor-pointer transition-all hover:bg-blue-50/50 min-h-[250px]">
             <Plus size={48} strokeWidth={1} />
             <span className="font-black text-[10px] uppercase mt-2 tracking-widest">{t('workflow.create_path')}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowList;